import { useState, type CSSProperties } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { selfCheckApi, type ListSelfCheckQuery, type SelfCheckCategory } from '@/api/self-check';
import { extractApiError } from '@/api/client';
import { kstDateTime } from '@/lib/format-date';

// round7 §1-7 (D9) — 셀프 체크 위저드 로그 열람(ADMIN).
//
// 공개 포털의 접수 전 셀프 체크에서 사용자가 거친 질문/선택 경로와 자가 해결
// 여부를 본다. 운영팀이 자주 묻는 문제·핸드오프율을 파악하는 read-only 페이지.

const CATEGORY_LABEL: Record<SelfCheckCategory, string> = {
  NOT_WORKING: '작동 안 함',
  GRILL_ISSUE: '그릴 / 불판',
  SELECTOR: '간택기',
  OTHER: '기타 / 점검',
};

const PAGE_SIZE = 20;

export function SelfCheckLogsPage() {
  const [category, setCategory] = useState<SelfCheckCategory | ''>('');
  const [resolved, setResolved] = useState<'' | 'true' | 'false'>('');
  const [serial, setSerial] = useState('');
  const [page, setPage] = useState(1);

  const query: ListSelfCheckQuery = {
    page,
    pageSize: PAGE_SIZE,
    ...(category ? { category } : {}),
    ...(resolved ? { resolved: resolved === 'true' } : {}),
    ...(serial.trim() ? { serial: serial.trim() } : {}),
  };

  const q = useQuery({
    queryKey: ['admin', 'self-check-logs', query],
    queryFn: () => selfCheckApi.list(query),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const totalPages = q.data ? Math.max(1, Math.ceil(q.data.total / PAGE_SIZE)) : 1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header>
        <h1 className="kuun-h2" style={{ margin: 0 }}>
          셀프 체크 로그
        </h1>
      </header>

      {/* 필터 */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <select
          value={category}
          onChange={(e) => {
            setCategory(e.target.value as SelfCheckCategory | '');
            setPage(1);
          }}
          style={selectStyle}
          data-testid="self-check-category-filter"
        >
          <option value="">전체 카테고리</option>
          {(Object.keys(CATEGORY_LABEL) as SelfCheckCategory[]).map((c) => (
            <option key={c} value={c}>
              {CATEGORY_LABEL[c]}
            </option>
          ))}
        </select>
        <select
          value={resolved}
          onChange={(e) => {
            setResolved(e.target.value as '' | 'true' | 'false');
            setPage(1);
          }}
          style={selectStyle}
          data-testid="self-check-resolved-filter"
        >
          <option value="">전체 결과</option>
          <option value="true">자가 해결</option>
          <option value="false">접수 권장</option>
        </select>
        <input
          value={serial}
          onChange={(e) => setSerial(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setPage(1);
          }}
          placeholder="SN 검색"
          style={{ ...selectStyle, width: 160 }}
          data-testid="self-check-serial-filter"
        />
      </div>

      {q.error && (
        <div
          style={{
            padding: 12,
            background: 'var(--bg-danger-subtle)',
            border: '1px solid var(--fg-danger)',
            borderRadius: 'var(--r-card)',
            color: 'var(--fg-danger)',
            fontSize: 13,
          }}
        >
          {extractApiError(q.error)}
        </div>
      )}

      <div className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
        {q.isFetching ? '불러오는 중…' : `${q.data?.total ?? 0} 건`}
      </div>

      {q.data && q.data.items.length === 0 && !q.isFetching && (
        <div className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
          기록이 없습니다.
        </div>
      )}

      {q.data && q.data.items.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={th}>시각</th>
              <th style={th}>카테고리</th>
              <th style={th}>SN</th>
              <th style={th}>결과</th>
              <th style={th}>경로</th>
            </tr>
          </thead>
          <tbody>
            {q.data.items.map((row) => (
              <tr key={row.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                <td
                  style={{
                    ...td,
                    fontFamily: 'var(--font-mono)',
                    fontSize: 11,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {/* 추가수정 2차(26-07-27) §2 — 브라우저 로캘 대신 KST 고정.
                      제출 시각 자체가 이 로그의 알맹이라 시각까지 남긴다. */}
                  {kstDateTime(row.createdAt) ?? '–'}
                </td>
                <td style={td}>
                  {CATEGORY_LABEL[row.category as SelfCheckCategory] ?? row.category}
                </td>
                <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                  {row.serial ?? '–'}
                </td>
                <td style={td}>
                  <span
                    style={{
                      fontSize: 11,
                      padding: '1px 8px',
                      borderRadius: 'var(--r-pill)',
                      background: row.resolved
                        ? 'var(--bg-success-subtle)'
                        : 'var(--bg-warning-subtle)',
                      color: row.resolved
                        ? 'var(--fg-success)'
                        : 'var(--fg-warning, var(--fg-default))',
                      fontWeight: 600,
                    }}
                  >
                    {row.resolved ? '자가 해결' : '접수 권장'}
                  </span>
                </td>
                <td style={{ ...td, color: 'var(--fg-muted)', fontSize: 12 }}>
                  {row.path.map((p) => `${p.question} → ${p.answer}`).join('  ·  ') || '–'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* 페이지네이션 */}
      {q.data && q.data.total > PAGE_SIZE && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center' }}>
          <button
            type="button"
            className="kuun-btn-ghost"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            이전
          </button>
          <span className="kuun-caption">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            className="kuun-btn-ghost"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            다음
          </button>
        </div>
      )}
    </div>
  );
}

const selectStyle: CSSProperties = {
  height: 32,
  padding: '0 10px',
  fontSize: 13,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-input)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
};

const th: CSSProperties = {
  textAlign: 'left',
  padding: '6px 8px',
  fontSize: 11,
  color: 'var(--fg-muted)',
  fontWeight: 600,
  borderBottom: '1px solid var(--border-subtle)',
};

const td: CSSProperties = {
  padding: '8px',
  verticalAlign: 'top',
};
